import { env } from './config.ts';

/**
 * Lets testers and automated tests opt in to a non-default login provider, e.g. the fake BankID
 * (MOCK_SERVICE, served by scoopr-mock-service) that skips Scrive and its rate limits.
 *
 * Open any page with `?login_provider=MOCK_SERVICE` to opt in, and `?login_provider=off` to opt out.
 * The choice is kept in localStorage, so it survives logout and new tabs. Release builds ignore it.
 * Local builds always use MOCK_SERVICE, there's no real BankID callback to localhost.
 */
const SEARCH_PARAM = 'login_provider';
const STORAGE_KEY = 'SCOOPR_LOGIN_PROVIDER';
const OPT_IN_PROVIDERS = ['MOCK_SERVICE'] as const;

type OptInLoginProvider = (typeof OPT_IN_PROVIDERS)[number];

const isOptInProvider = (value: string | null): value is OptInLoginProvider =>
  OPT_IN_PROVIDERS.includes(value as OptInLoginProvider);

/** Call once on app start, it stores or clears the opt-in from the `login_provider` search param */
export const rememberLoginProviderFromUrl = () => {
  if (env === 'release') {
    return;
  }
  const value = new URLSearchParams(window.location.search).get(SEARCH_PARAM);
  if (value === null) {
    return;
  }
  try {
    if (isOptInProvider(value)) {
      localStorage.setItem(STORAGE_KEY, value);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage can be blocked, the default provider is used then
  }
};

/** The provider the user opted in to, `undefined` means the app's default */
export const getLoginProviderOverride = (): OptInLoginProvider | undefined => {
  if (env === 'local') {
    return 'MOCK_SERVICE';
  }
  if (env === 'release') {
    return undefined;
  }
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isOptInProvider(value) ? value : undefined;
  } catch {
    return undefined;
  }
};
